import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@atgfr8/atg-ui-tailwind';
import type { BulkSharedCapacityOutcome } from '@store/loadboard/bulkSharedCapacity';
import type { BulkSharedCapacityResult } from '@store/loadboard/types';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount } from '@utils/utils';
import { For, Show, type JSX } from 'solid-js';

const displayLoadNumber = (loadNumber: string) =>
  loadNumber.length > 0 ? loadNumber : '—';

const Section = (props: {
  title: string;
  testId: string;
  children: JSX.Element;
}) => (
  <section
    class="mb-4"
    data-testid={formatDataTestId(props.testId, 'div')}
    aria-label={props.title}
  >
    <h3 class="mb-2 text-sm font-semibold text-armstrong-primary">
      {props.title}
    </h3>
    {props.children}
  </section>
);

export const ShareCapacityResultReview = (props: {
  result: BulkSharedCapacityResult | undefined;
  outcome: BulkSharedCapacityOutcome | undefined;
}) => (
  <Show when={props.result}>
    {(result) => (
      <Show when={props.outcome}>
        {(outcome) => (
          <div class="min-h-0 flex-1 overflow-auto">
            <div class="mb-3 text-sm text-armstrong-primary" role="status">
              <Show when={result().success}>
                {outcome().successes.length} loads assigned to shared capacity.
              </Show>
              <Show when={result().partialSuccess}>
                {outcome().successes.length} loads were assigned. Review the
                loads that failed. Assigned loads stay shared even when a later
                error is returned.
              </Show>
              <Show when={!result().success && !result().partialSuccess}>
                No loads were assigned.
              </Show>
            </div>
            <Show when={result().results.length > 0}>
              <Section title="Assignment details" testId="Assignment details">
                <p class="mb-2 text-sm text-armstrong-primary">
                  Capacity group, assignee, and Max Buy returned for the
                  assigned loads. These values are not paired to a load number.
                </p>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Capacity Group</TableHeaderCell>
                      <TableHeaderCell>Assigned To</TableHeaderCell>
                      <TableHeaderCell>Max Buy</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <For each={result().results}>
                      {(item) => (
                        <TableRow>
                          <TableCell>{item.capacityGroupName ?? '—'}</TableCell>
                          <TableCell>{item.assignedName ?? '—'}</TableCell>
                          <TableCell>{formatAmount(item.maxBuyRate)}</TableCell>
                        </TableRow>
                      )}
                    </For>
                  </TableBody>
                </Table>
              </Section>
            </Show>
            <Show when={outcome().failures.length > 0}>
              <Section title="Failed loads" testId="Failed loads">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Load Number</TableHeaderCell>
                      <TableHeaderCell>Error</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <For each={outcome().failures}>
                      {(load) => (
                        <TableRow>
                          <TableCell>
                            {displayLoadNumber(load.loadNumber)}
                          </TableCell>
                          <TableCell>{load.message}</TableCell>
                        </TableRow>
                      )}
                    </For>
                  </TableBody>
                </Table>
              </Section>
            </Show>
          </div>
        )}
      </Show>
    )}
  </Show>
);
